import { Link, useNavigate, useParams } from "react-router-dom";
import { FiChevronRight } from "react-icons/fi";
import AppBreadcrumbs from "components/common/AppBreadcrumbs";
import Card from "components/Card";
import ProjectBasicsCard from "pages/project/ProjectFormComponent/ProjectBasicsCard";
import ProjectFormActions from "pages/project/ProjectFormComponent/ProjectFormActions";
import ProjectStepper from "pages/project/ProjectFormComponent/ProjectStepper";
import {
  ProjectProvider,
  useProjectFormContext,
} from "pages/project/context/ProjectFormContext";
import { PROJECT_STEPS } from "pages/project/ProjectFormComponent/steps/projectSteps";
import StepCompanyOverview from "pages/project/ProjectFormComponent/steps/StepCompanyOverview";
import StepOfferingDocument from "pages/project/ProjectFormComponent/steps/StepOfferingDocument";
import StepProjectOverview from "pages/project/ProjectFormComponent/steps/StepProjectOverview";
import StepNewsUpdates from "pages/project/ProjectFormComponent/steps/StepNewsUpdates";
import StepTeamDescription from "pages/project/ProjectFormComponent/steps/StepTeamDescription";

const STEP_COMPONENTS = {
  overview: StepProjectOverview,
  company: StepCompanyOverview,
  team: StepTeamDescription,
  offering: StepOfferingDocument,
  newsUpdates: StepNewsUpdates,
};

function editBreadcrumbItem(existingProjectId) {
  return {
    label: "Edit project",
    path: existingProjectId ? `/projects/${existingProjectId}/edit` : "#",
  };
}

function ProjectEditLoadErrorView({ projectLoadError, existingProjectId }) {
  return (
    <div className="flex min-h-[min(80vh,calc(100vh-8rem))] flex-col space-y-6 pb-28 md:pb-24">
      <AppBreadcrumbs
        items={[{ label: "Projects", path: "/projects" }, editBreadcrumbItem(existingProjectId)]}
      />
      <Card className="border-slate-200">
        <p className="text-sm font-medium text-red-800" role="alert">
          {projectLoadError}
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link
            to="/projects"
            className="inline-flex rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-text-700 transition hover:bg-slate-50"
          >
            Back to projects
          </Link>
          {existingProjectId ? (
            <Link
              to={`/projects/${existingProjectId}`}
              className="inline-flex rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-text-700 transition hover:bg-slate-50"
            >
              View project
            </Link>
          ) : null}
        </div>
      </Card>
    </div>
  );
}

function ProjectEditLoadingView({ existingProjectId }) {
  return (
    <div className="flex min-h-[min(80vh,calc(100vh-8rem))] flex-col space-y-6 pb-28 md:pb-24">
      <AppBreadcrumbs
        items={[{ label: "Projects", path: "/projects" }, editBreadcrumbItem(existingProjectId)]}
      />
      <Card className="border-slate-200">
        <p className="text-sm text-text-600">Loading project for editing…</p>
      </Card>
    </div>
  );
}

function ProjectCreateFormMain({
  currentStepIndex,
  currentStepId,
  stepCount,
  goToStep,
  submitError,
  isSubmitting,
  isEditMode,
  existingProjectId,
  isFirst,
  isLast,
  onCancel,
  onBack,
  onNext,
  onSubmit,
  stepContent,
}) {
  const secondCrumb =
    isEditMode && existingProjectId
      ? { label: "Edit project", path: `/projects/${existingProjectId}/edit` }
      : { label: "Add project", path: "/projects/new" };

  return (
    <div className="flex min-h-[min(80vh,calc(100vh-8rem))] flex-col space-y-6 pb-28 md:pb-24">
      <AppBreadcrumbs items={[{ label: "Projects", path: "/projects" }, secondCrumb]} />

      <Card className="border-slate-200">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-lg font-semibold text-text-900 md:text-xl">
              {isEditMode ? "Edit project" : "Create project"}
            </h1>
            <p className="mt-1 max-w-xl text-sm text-text-500">
              {isEditMode
                ? "Review each section and save when you are done. Current values are loaded from the server."
                : "Complete each section, then submit to create the project."}
            </p>
          </div>
          <div className="hidden items-center gap-1 text-xs font-medium text-text-500 sm:flex">
            Step {currentStepIndex + 1} of {stepCount}
            <FiChevronRight className="h-4 w-4" aria-hidden />
            <span className="text-text-700">{PROJECT_STEPS[currentStepIndex]?.label}</span>
          </div>
        </div>
      </Card>

      {submitError ? (
        <div
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-800"
          role="alert"
        >
          {submitError}
        </div>
      ) : null}

      <ProjectBasicsCard />

      <Card className="border-slate-200">
        <div className="-mt-2 mb-6">
          <ProjectStepper currentIndex={currentStepIndex} onStepChange={goToStep} />
        </div>

        <div
          key={currentStepId}
          className="min-h-[200px] transition-opacity duration-300 motion-reduce:transition-none"
        >
          {stepContent}
        </div>
      </Card>

      <ProjectFormActions
        isFirstStep={isFirst}
        isLastStep={isLast}
        isSubmitting={isSubmitting}
        submitLabel={isEditMode ? "Save changes" : "Submit project"}
        submittingLabel={isEditMode ? "Saving…" : "Submitting…"}
        onCancel={onCancel}
        onBack={onBack}
        onNext={onNext}
        onSubmit={onSubmit}
      />
    </div>
  );
}

function ProjectCreateForm() {
  const navigate = useNavigate();
  const {
    currentStepIndex,
    currentStepId,
    stepCount,
    goNext,
    goBack,
    goToStep,
    submitProject,
    submitError,
    isSubmitting,
    isEditMode,
    existingProjectId,
    isLoadingProject,
    projectLoadError,
  } = useProjectFormContext();

  const isFirst = currentStepIndex === 0;
  const isLast = currentStepIndex === stepCount - 1;

  const onCancel = () => {
    navigate("/projects");
  };

  const onNext = () => {
    goNext();
  };

  const onSubmit = async () => {
    const result = await submitProject();
    if (!result?.ok) return;
    if (isEditMode && existingProjectId) {
      navigate(`/projects/${existingProjectId}`, { state: { projectUpdated: true } });
      return;
    }
    if (result.createdId) {
      navigate(`/projects/${result.createdId}`, { state: { projectCreated: true } });
      return;
    }
    navigate("/projects", { state: { projectCreated: true } });
  };

  const StepComponent = STEP_COMPONENTS[currentStepId] ?? STEP_COMPONENTS.overview;
  const stepContent = <StepComponent />;

  if (isEditMode && projectLoadError) {
    return <ProjectEditLoadErrorView projectLoadError={projectLoadError} existingProjectId={existingProjectId} />;
  }

  if (isEditMode && isLoadingProject) {
    return <ProjectEditLoadingView existingProjectId={existingProjectId} />;
  }

  return (
    <ProjectCreateFormMain
      currentStepIndex={currentStepIndex}
      currentStepId={currentStepId}
      stepCount={stepCount}
      goToStep={goToStep}
      submitError={submitError}
      isSubmitting={isSubmitting}
      isEditMode={isEditMode}
      existingProjectId={existingProjectId}
      isFirst={isFirst}
      isLast={isLast}
      onCancel={onCancel}
      onBack={goBack}
      onNext={onNext}
      onSubmit={onSubmit}
      stepContent={stepContent}
    />
  );
}

export default function ProjectCreatePage() {
  const { projectId } = useParams();
  const editId = projectId && projectId !== "new" ? projectId : null;

  return (
    <ProjectProvider key={editId || "new"} existingProjectId={editId}>
      <ProjectCreateForm />
    </ProjectProvider>
  );
}
